import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CreditCardLogos from '../Common/CreditCardLogos';
import { useCardInput, useCVVInput, useExpiryInput } from '../../hooks/useCardInput';
import { detectCardType } from '../../utils/cardDetection';
import { formatTravelDate } from '../../utils/dateUtils';
import { 
  COUNTRIES, 
  getCountryInfo, 
  getPostalLabel, 
  getPostalPlaceholder, 
  getStateLabel, 
  isStateRequired, 
  validatePostalCode,
  formatPostalCode 
} from '../../utils/countryUtils';
import './PublicPaymentPage.css';
import './PaymentForm.css';
import PaymentStateScreen from './PaymentStateScreen';
import { classifyPaymentMessage } from '../../utils/paymentState.mjs';

// Create axios instance without auth interceptors for public routes
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api'
});

const CheckIcon = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>);
const LockIcon = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>);
const money = (value) => parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PaymentLinkPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [paymentLink, setPaymentLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSecondPayment, setShowSecondPayment] = useState(false);
  const [showGiftCard, setShowGiftCard] = useState(false);
  const [showCreditCard, setShowCreditCard] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  
  const [primaryPayment, setPrimaryPayment] = useState({
    cardNumber: '',
    cardName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingCountry: 'US'
  });
  
  const [secondaryPayment, setSecondaryPayment] = useState({
    cardNumber: '',
    cardName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    amount: ''
  });

  const [contact, setContact] = useState({
    email: '',
    phone: ''
  });

  const [giftCard, setGiftCard] = useState({
    cardType: '',
    cardNumber: '',
    pin: '',
    amount: ''
  });

  // Credit card input hooks for enhanced UX
  const primaryCardInput = useCardInput(primaryPayment.cardNumber, (cardData) => {
    setPrimaryPayment(prev => ({
      ...prev,
      cardNumber: cardData.cardNumber
    }));
  });

  const primaryCVVInput = useCVVInput(primaryCardInput.detectedCardType, primaryPayment.cvv);
  const primaryExpiryInput = useExpiryInput(primaryPayment.expirationMonth && primaryPayment.expirationYear ? `${primaryPayment.expirationMonth}/${primaryPayment.expirationYear}` : '');

  const secondaryCardInput = useCardInput(secondaryPayment.cardNumber, (cardData) => {
    setSecondaryPayment(prev => ({
      ...prev,
      cardNumber: cardData.cardNumber
    }));
  });

  const secondaryCVVInput = useCVVInput(secondaryCardInput.detectedCardType, secondaryPayment.cvv);
  const secondaryExpiryInput = useExpiryInput(secondaryPayment.expirationMonth && secondaryPayment.expirationYear ? `${secondaryPayment.expirationMonth}/${secondaryPayment.expirationYear}` : '');

  useEffect(() => {
    fetchPaymentLink();
  }, [token]);

  const fetchPaymentLink = async () => {
    try {
      setLoading(true);
      
      // Try Independent Agent payment links first (they use longer hex tokens)
      let response;
      try {
        response = await publicAxios.get(`/public/independent-payment-links/${token}`);
      } catch (indError) {
        // If not found, try Agency Agent payment links
        if (indError.response?.status === 404) {
          response = await publicAxios.get(`/public/payment-links/${token}`);
        } else {
          throw indError;
        }
      }
      
      if (response.data.success) {
        const linkData = response.data.data;
        setPaymentLink(linkData);
        
        // Pre-fill contact information if available
        setContact({
          email: linkData.client_email || '',
          phone: linkData.client_phone || ''
        });
      } else {
        setSubmitError(response.data.message || 'Failed to load payment link');
      }
    } catch (error) {
      console.error('Error fetching payment link:', error);
      if (error.response?.data?.message) {
        setSubmitError(error.response.data.message);
      } else {
        setSubmitError('Failed to load payment link. Please check the URL and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Require at least one payment method
    if (!showCreditCard && !showGiftCard) {
      newErrors.general = 'Please provide at least one payment method (Credit Card or Gift Card)';
      setErrors(newErrors);
      return false;
    }

    // Primary card validation (only if credit card is shown)
    if (showCreditCard) {
      if (!primaryCardInput.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!/^\d+$/.test(primaryCardInput.cardNumber)) {
        newErrors.cardNumber = 'Card number must contain only numbers';
      } else if (primaryCardInput.cardNumber.length < 13) {
        newErrors.cardNumber = 'Card number must be at least 13 digits';
      } else if (!primaryCardInput.isValid) {
        newErrors.cardNumber = 'Please enter a valid card number';
      }
      if (!primaryPayment.cardName.trim()) {
        newErrors.cardName = 'Cardholder name is required';
      }
      if (!primaryExpiryInput.isValid) {
        newErrors.expirationMonth = 'Valid expiration date is required';
      }
      if (!primaryCVVInput.isValid) {
        newErrors.cvv = 'Valid CVV is required';
      }

      // Billing address validation (only for credit card)
      if (!primaryPayment.billingAddress.trim()) {
        newErrors.billingAddress = 'Billing address is required';
      }
      if (!primaryPayment.billingCity.trim()) {
        newErrors.billingCity = 'City is required';
      }
      if (isStateRequired(primaryPayment.billingCountry) && !primaryPayment.billingState.trim()) {
        newErrors.billingState = `${getStateLabel(primaryPayment.billingCountry)} is required`;
      }
      if (!primaryPayment.billingZip.trim()) {
        newErrors.billingZip = `${getPostalLabel(primaryPayment.billingCountry)} is required`;
      } else if (!validatePostalCode(primaryPayment.billingZip, primaryPayment.billingCountry)) {
        newErrors.billingZip = `Invalid ${getPostalLabel(primaryPayment.billingCountry).toLowerCase()} format`;
      }
    }

    // Gift card validation (only if gift card is shown)
    if (showGiftCard) {
      if (!giftCard.cardNumber.trim()) {
        newErrors.giftCardNumber = 'Gift card number is required';
      }
      // Amount is required if splitting payment with credit card
      if (showCreditCard && (!giftCard.amount || parseFloat(giftCard.amount) <= 0)) {
        newErrors.giftCardAmount = 'Valid amount is required when splitting payment';
      }
    }

    // Secondary payment validation
    if (showSecondPayment) {
      if (!secondaryCardInput.cardNumber) {
        newErrors.secondaryCardNumber = 'Card number is required';
      } else if (!/^\d+$/.test(secondaryCardInput.cardNumber)) {
        newErrors.secondaryCardNumber = 'Card number must contain only numbers';
      } else if (secondaryCardInput.cardNumber.length < 13) {
        newErrors.secondaryCardNumber = 'Card number must be at least 13 digits';
      } else if (!secondaryCardInput.isValid) {
        newErrors.secondaryCardNumber = 'Please enter a valid card number';
      }
      if (!secondaryPayment.cardName.trim()) {
        newErrors.secondaryCardName = 'Cardholder name is required';
      }
      if (!secondaryExpiryInput.isValid) {
        newErrors.secondaryExpirationMonth = 'Valid expiration date is required';
      }
      if (!secondaryCVVInput.isValid) {
        newErrors.secondaryCvv = 'Valid CVV is required';
      }
      if (!secondaryPayment.amount || parseFloat(secondaryPayment.amount) <= 0) {
        newErrors.secondaryAmount = 'Valid amount is required';
      }
    }

    // Contact validation
    if (!contact.email.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(contact.email)) {
      newErrors.contactEmail = 'Valid email is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      const paymentData = {
        contact,
        hasCreditCard: showCreditCard,
        hasGiftCard: showGiftCard,
        hasSecondaryPayment: showSecondPayment
      };

      // Add credit card data if provided
      if (showCreditCard) {
        paymentData.primaryCard = {
          ...primaryPayment,
          cardNumber: primaryCardInput.cardNumber,
          expirationMonth: primaryExpiryInput.expiry.split('/')[0] || primaryPayment.expirationMonth,
          expirationYear: primaryExpiryInput.expiry.split('/')[1] || primaryPayment.expirationYear,
          cvv: primaryCVVInput.cvv,
          billingAddress: {
            address: primaryPayment.billingAddress,
            city: primaryPayment.billingCity,
            state: primaryPayment.billingState,
            zip: primaryPayment.billingZip,
            country: primaryPayment.billingCountry
          }
        };
      }

      // Add gift card data if provided
      if (showGiftCard) {
        paymentData.giftCard = {
          cardType: giftCard.cardType,
          cardNumber: giftCard.cardNumber,
          pin: giftCard.pin,
          amount: giftCard.amount || paymentLink.amount
        };
      }

      if (showSecondPayment) {
        paymentData.secondaryCard = {
          ...secondaryPayment,
          cardNumber: secondaryCardInput.cardNumber,
          expirationMonth: secondaryExpiryInput.expiry.split('/')[0] || secondaryPayment.expirationMonth,
          expirationYear: secondaryExpiryInput.expiry.split('/')[1] || secondaryPayment.expirationYear,
          cvv: secondaryCVVInput.cvv
        };
      }

      // Try Independent Agent payment links first, then Agency payment links
      let response;
      try {
        response = await publicAxios.post(`/public/independent-payment-links/${token}/submit`, paymentData);
      } catch (indError) {
        if (indError.response?.status === 404) {
          response = await publicAxios.post(`/public/payment-links/${token}/submit`, paymentData);
        } else {
          throw indError;
        }
      }

      if (response.data.success) {
        // Redirect to success page
        navigate(`/payment-success/${response.data.confirmation_number}`);
      } else {
        setSubmitError(response.data.message || 'Failed to submit payment information');
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      if (error.response?.data?.message) {
        setSubmitError(error.response.data.message);
      } else {
        setSubmitError('Failed to submit payment information. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (value, isPrimary = true) => {
    const formatted = formatCardNumber(value);
    if (isPrimary) {
      setPrimaryPayment({...primaryPayment, cardNumber: formatted.replace(/\s/g, '')});
    } else {
      setSecondaryPayment({...secondaryPayment, cardNumber: formatted.replace(/\s/g, '')});
    }
  };

  if (loading) {
    return <PaymentStateScreen variant="loading" />;
  }

  if (submitError && !paymentLink) {
    const variant = classifyPaymentMessage(submitError);
    const isGeneric = submitError.startsWith('Failed to load payment link');
    return (
      <PaymentStateScreen
        variant={variant}
        message={variant === 'error' && !isGeneric ? submitError : undefined}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="pf-page">
      <div className="pf-container">
        <div className="pf-banner">
          <div className="pf-banner-inner">
            <div className="pf-banner-title">
              <div className="pf-banner-tile" aria-hidden="true">
                <svg viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>
              </div>
              <h1>Secure Payment</h1>
            </div>
            <p className="pf-banner-sub">Complete your payment for <strong>{paymentLink.client_name}</strong></p>
            <div className="pf-banner-secure">
              <LockIcon />
              SSL Encrypted &amp; Secure
            </div>
          </div>
        </div>

        <div className="pf-grid">
          <aside className="pf-summary">
            <h2>Payment Summary</h2>
            <div className="pf-summary-lines">
              <div className="pf-summary-line">
                <span>Client:</span>
                <span>{paymentLink.client_name}</span>
              </div>
              <div className="pf-summary-line">
                <span>Payment Type:</span>
                <span className="pf-cap">{paymentLink.payment_type.replace('_', ' ')}</span>
              </div>
              {paymentLink.description && (
                <div className="pf-summary-line">
                  <span>Description:</span>
                  <span>{paymentLink.description}</span>
                </div>
              )}
              <div className="pf-summary-line">
                <span>Travel Agent:</span>
                <span>{paymentLink.agent_name}</span>
              </div>
            </div>
            <div className="pf-summary-divider" />
            <div className="pf-summary-total">
              <span>Total Amount:</span>
              <span className="pf-amount">${money(paymentLink.amount)}</span>
            </div>

            {paymentLink.custom_message && (
              <div className="pf-note">
                <strong>Message from your agent:</strong>
                {paymentLink.custom_message}
              </div>
            )}

            <div className="pf-brand">
              <div className="pf-brand-mark" aria-hidden="true">
                <svg viewBox="0 0 24 24"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
              </div>
              <span>Powered by <strong>Voyager Pro</strong></span>
            </div>
          </aside>

          <div className="pf-form">
            {submitError && (
              <div className="pf-alert" role="alert">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
          {/* Contact Information */}
          <div className="pf-section">
            <h2>Contact Information</h2>
            
            <div className="pf-row">
              <div className="pf-field">
                <label>Email Address<span className="pf-req">*</span></label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({...contact, email: e.target.value})}
                  className={errors.contactEmail ? 'is-invalid' : ''}
                  required
                />
                {errors.contactEmail && <span className="pf-error">{errors.contactEmail}</span>}
              </div>
              <div className="pf-field">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e) => setContact({...contact, phone: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="pf-section">
            <h2>Payment Method</h2>
            <div className="pf-toggles">
              <button
                type="button"
                className={`pf-toggle ${showCreditCard ? 'is-active' : ''}`}
                onClick={() => setShowCreditCard(!showCreditCard)}
              >
                {showCreditCard && <CheckIcon />}Credit Card
              </button>
              <button
                type="button"
                className={`pf-toggle ${showGiftCard ? 'is-active' : ''}`}
                onClick={() => setShowGiftCard(!showGiftCard)}
              >
                {showGiftCard && <CheckIcon />}Gift Card
              </button>
            </div>
            {errors.general && <span className="pf-error">{errors.general}</span>}
          </div>

          {/* Primary Payment */}
          {showCreditCard && (
          <div className="pf-section">
            <h2>Credit Card Information</h2>
            
            <div className="pf-row">
              <div className="pf-field">
                <label>Card Number<span className="pf-req">*</span></label>
                <input
                  {...primaryCardInput.getInputProps()}
                  className={errors.cardNumber ? 'is-invalid' : ''}
                  required
                />
                <CreditCardLogos 
                  detectedCardType={primaryCardInput.detectedCardType}
                  size="medium"
                  showLabels={true}
                />
                {errors.cardNumber && <span className="pf-error">{errors.cardNumber}</span>}
              </div>
            </div>

            <div className="pf-row">
              <div className="pf-field">
                <label>Cardholder Name<span className="pf-req">*</span></label>
                <input
                  type="text"
                  value={primaryPayment.cardName}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, cardName: e.target.value})}
                  className={errors.cardName ? 'is-invalid' : ''}
                  required
                />
                {errors.cardName && <span className="pf-error">{errors.cardName}</span>}
              </div>
            </div>

            <div className="pf-row">
              <div className="pf-field">
                <label>Expiry Date<span className="pf-req">*</span></label>
                <input
                  {...primaryExpiryInput.getExpiryInputProps()}
                  className={errors.expirationMonth || errors.expirationYear ? 'is-invalid' : ''}
                  required
                />
                {(errors.expirationMonth || errors.expirationYear) && (
                  <span className="pf-error">
                    {errors.expirationMonth || errors.expirationYear}
                  </span>
                )}
              </div>
              <div className="pf-field">
                <label>CVV<span className="pf-req">*</span> {primaryCardInput.detectedCardType?.type === 'amex' ? '(4 digits)' : '(3 digits)'}</label>
                <input
                  {...primaryCVVInput.getCVVInputProps()}
                  className={errors.cvv ? 'is-invalid' : ''}
                  required
                />
                {errors.cvv && <span className="pf-error">{errors.cvv}</span>}
              </div>
            </div>

            {/* Billing Address */}
            <h3>Billing Address</h3>
            
            <div className="pf-field">
              <label>Country<span className="pf-req">*</span></label>
              <select
                value={primaryPayment.billingCountry}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingCountry: e.target.value})}
               
                required
              >
                {COUNTRIES.map(country => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="pf-field">
              <label>Street Address<span className="pf-req">*</span></label>
              <input
                type="text"
                value={primaryPayment.billingAddress}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingAddress: e.target.value})}
                className={errors.billingAddress ? 'is-invalid' : ''}
                required
              />
              {errors.billingAddress && <span className="pf-error">{errors.billingAddress}</span>}
            </div>

            <div className="pf-row">
              <div className="pf-field">
                <label>City<span className="pf-req">*</span></label>
                <input
                  type="text"
                  value={primaryPayment.billingCity}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, billingCity: e.target.value})}
                  className={errors.billingCity ? 'is-invalid' : ''}
                  required
                />
                {errors.billingCity && <span className="pf-error">{errors.billingCity}</span>}
              </div>
              <div className="pf-field">
                <label>
                  {getStateLabel(primaryPayment.billingCountry)}
                  {isStateRequired(primaryPayment.billingCountry) ? <span className="pf-req">*</span> : ''}
                </label>
                <input
                  type="text"
                  value={primaryPayment.billingState}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, billingState: e.target.value})}
                  maxLength={primaryPayment.billingCountry === 'US' ? '2' : '50'}
                  placeholder={primaryPayment.billingCountry === 'US' ? 'CA' : 'Optional'}
                  className={errors.billingState ? 'is-invalid' : ''}
                  required={isStateRequired(primaryPayment.billingCountry)}
                />
                {errors.billingState && <span className="pf-error">{errors.billingState}</span>}
              </div>
              <div className="pf-field">
                <label>{getPostalLabel(primaryPayment.billingCountry)}<span className="pf-req">*</span></label>
                <input
                  type="text"
                  value={primaryPayment.billingZip}
                  onChange={(e) => {
                    const formatted = formatPostalCode(e.target.value, primaryPayment.billingCountry);
                    setPrimaryPayment({...primaryPayment, billingZip: formatted});
                  }}
                  placeholder={getPostalPlaceholder(primaryPayment.billingCountry)}
                  className={errors.billingZip ? 'is-invalid' : ''}
                  required
                />
                {errors.billingZip && <span className="pf-error">{errors.billingZip}</span>}
              </div>
            </div>
          </div>
          )}

          {/* Gift Card Section */}
          {showGiftCard && (
          <div className="pf-section">
            <h2>Gift Card Information</h2>
            
            <div className="pf-field">
              <label>Card Type (Optional)</label>
              <input
                type="text"
                value={giftCard.cardType}
                onChange={(e) => setGiftCard({...giftCard, cardType: e.target.value})}
               
                placeholder="e.g., Disney, Visa Gift Card, Carnival Gift Card"
              />
              <small className="pf-hint">Enter the type of gift card for reference</small>
            </div>

            <div className="pf-field">
              <label>Gift Card Number<span className="pf-req">*</span></label>
              <input
                type="text"
                value={giftCard.cardNumber}
                onChange={(e) => setGiftCard({...giftCard, cardNumber: e.target.value})}
                className={errors.giftCardNumber ? 'is-invalid' : ''}
                placeholder="Enter gift card number"
                required
              />
              {errors.giftCardNumber && <span className="pf-error">{errors.giftCardNumber}</span>}
            </div>

            <div className="pf-field">
              <label>PIN/Security Code (Optional)</label>
              <input
                type="text"
                value={giftCard.pin}
                onChange={(e) => setGiftCard({...giftCard, pin: e.target.value})}
               
                placeholder="Enter PIN if applicable"
              />
              <small className="pf-hint">Some gift cards require a PIN or security code</small>
            </div>

            {showCreditCard && (
              <div className="pf-field">
                <label>Amount from Gift Card<span className="pf-req">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  value={giftCard.amount}
                  onChange={(e) => setGiftCard({...giftCard, amount: e.target.value})}
                  className={errors.giftCardAmount ? 'is-invalid' : ''}
                  placeholder="Amount to apply from this gift card"
                  required
                />
                {errors.giftCardAmount && <span className="pf-error">{errors.giftCardAmount}</span>}
                <small className="pf-hint">Enter the amount to apply from this gift card when splitting payment</small>
              </div>
            )}
          </div>
          )}

          {/* Secondary Payment Option */}
          {showCreditCard && (
          <div className="pf-split">
            <div className="pf-split-toggle">
              <label className="pf-split-label">
                <input
                  type="checkbox"
                  checked={showSecondPayment}
                  onChange={(e) => setShowSecondPayment(e.target.checked)}
                />
                Split payment between two cards
              </label>
            </div>

            {showSecondPayment && (
              <div className="pf-split-body">
                <h3>Second Payment Method</h3>
                
                <div className="pf-field">
                  <label>Amount for Second Card<span className="pf-req">*</span></label>
                  <input
                    type="number"
                    step="0.01"
                    max={paymentLink.amount}
                    value={secondaryPayment.amount}
                    onChange={(e) => setSecondaryPayment({...secondaryPayment, amount: e.target.value})}
                    className={errors.secondaryAmount ? 'is-invalid' : ''}
                    required
                  />
                  {errors.secondaryAmount && <span className="pf-error">{errors.secondaryAmount}</span>}
                </div>

                <div className="pf-row">
                  <div className="pf-field">
                    <label>Card Number<span className="pf-req">*</span></label>
                    <input
                      {...secondaryCardInput.getInputProps()}
                      className={errors.secondaryCardNumber ? 'is-invalid' : ''}
                      required
                    />
                    <CreditCardLogos 
                      detectedCardType={secondaryCardInput.detectedCardType}
                      size="medium"
                      showLabels={true}
                    />
                    {errors.secondaryCardNumber && <span className="pf-error">{errors.secondaryCardNumber}</span>}
                  </div>
                </div>

                <div className="pf-row">
                  <div className="pf-field">
                    <label>Cardholder Name<span className="pf-req">*</span></label>
                    <input
                      type="text"
                      value={secondaryPayment.cardName}
                      onChange={(e) => setSecondaryPayment({...secondaryPayment, cardName: e.target.value})}
                      className={errors.secondaryCardName ? 'is-invalid' : ''}
                      required
                    />
                    {errors.secondaryCardName && <span className="pf-error">{errors.secondaryCardName}</span>}
                  </div>
                </div>

                <div className="pf-row">
                  <div className="pf-field">
                    <label>Expiry Date<span className="pf-req">*</span></label>
                    <input
                      {...secondaryExpiryInput.getExpiryInputProps()}
                      className={errors.secondaryExpirationMonth || errors.secondaryExpirationYear ? 'is-invalid' : ''}
                      required
                    />
                    {(errors.secondaryExpirationMonth || errors.secondaryExpirationYear) && (
                      <span className="pf-error">
                        {errors.secondaryExpirationMonth || errors.secondaryExpirationYear}
                      </span>
                    )}
                  </div>
                  <div className="pf-field">
                    <label>CVV<span className="pf-req">*</span> {secondaryCardInput.detectedCardType?.type === 'amex' ? '(4 digits)' : '(3 digits)'}</label>
                    <input
                      {...secondaryCVVInput.getCVVInputProps()}
                      className={errors.secondaryCvv ? 'is-invalid' : ''}
                      required
                    />
                    {errors.secondaryCvv && <span className="pf-error">{errors.secondaryCvv}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          {paymentLink.agent_terms && (
            <div className="pf-terms">
              <h3>Agent Terms & Conditions</h3>
              <div className="pf-terms-body" dangerouslySetInnerHTML={{ __html: paymentLink.agent_terms }} />
            </div>
          )}

          <div className="pf-agreement">
            <p>
              By submitting your payment information you acknowledge that you have read and agreed to the Terms and Conditions.
            </p>
          </div>

              <button type="submit" className="pf-submit" disabled={submitting}>
                <LockIcon />
                {submitting ? 'Submitting...' : `Submit Payment Info ($${money(paymentLink.amount)})`}
              </button>
              <p className="pf-secure-note">
                <LockIcon />
                Your payment information is encrypted and secure.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentLinkPage;
